/**
 * Floor height (visual elevation) for a DungeonDef, plus authoring helpers for corridor
 * rises (stairs/ramps) and doors. Pure functions; the sim stays 2D and never reads heights.
 *
 * groundHeightAt rules:
 *  - inside a room polygon: the room's `elevation` (rooms are flat);
 *  - else inside a corridor strip: flat at the nearest rise end, linear across each rise
 *    (corridors without rises blend between their two rooms by distance);
 *  - else (walls, void): the height of the nearest walkable area.
 */
import type { CorridorDef, CorridorRise, DoorDef, DoorStyle, DungeonDef, EncounterId, Polygon, Rect, RoomDef, RoomId, Vec2, Yards } from '@keystone/sim';
import { DOOR_BLOCK_DEPTH } from '@keystone/sim';
import { pointInPolygon } from './geometry';

interface Area {
  poly: Polygon;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}
interface Prepared {
  rooms: (Area & { def: RoomDef })[];
  corridors: (Area & { def: CorridorDef })[];
  roomById: Map<RoomId, RoomDef>;
}

const prepared = new WeakMap<DungeonDef, Prepared>();

const toArea = (poly: Polygon): Area => {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const p of poly.points) {
    x0 = Math.min(x0, p.x);
    y0 = Math.min(y0, p.y);
    x1 = Math.max(x1, p.x);
    y1 = Math.max(y1, p.y);
  }
  return { poly, x0, y0, x1, y1 };
};

function prepare(d: DungeonDef): Prepared {
  let p = prepared.get(d);
  if (!p) {
    p = {
      rooms: d.rooms.map((def) => ({ ...toArea(def.shape), def })),
      corridors: d.corridors.map((def) => ({ ...toArea(def.shape), def })),
      roomById: new Map(d.rooms.map((r) => [r.id, r])),
    };
    prepared.set(d, p);
  }
  return p;
}

const inArea = (a: Area, p: Vec2): boolean => p.x >= a.x0 && p.x <= a.x1 && p.y >= a.y0 && p.y <= a.y1 && pointInPolygon(p, a.poly);

function closestOnSegment(p: Vec2, a: Vec2, b: Vec2): Vec2 {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  const t = l2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2));
  return { x: a.x + dx * t, y: a.y + dy * t };
}

function closestOnPolygon(p: Vec2, poly: Polygon): Vec2 {
  let best = poly.points[0] ?? p;
  let bd = Infinity;
  const pts = poly.points;
  for (let i = 0; i < pts.length; i++) {
    const q = closestOnSegment(p, pts[i]!, pts[(i + 1) % pts.length]!);
    const d = Math.hypot(q.x - p.x, q.y - p.y);
    if (d < bd) {
      bd = d;
      best = q;
    }
  }
  return best;
}

/** Distance from p to a polygon (0 inside). */
function distToPolygon(p: Vec2, poly: Polygon): number {
  if (pointInPolygon(p, poly)) return 0;
  const q = closestOnPolygon(p, poly);
  return Math.hypot(q.x - p.x, q.y - p.y);
}

const roomElevation = (r: RoomDef | undefined): Yards => r?.elevation ?? 0;

/** Height along a corridor at p (p is assumed to be in or near the strip). */
export function corridorHeightAt(dungeon: DungeonDef, c: CorridorDef, p: Vec2): Yards {
  const rises = c.rises;
  if (rises && rises.length > 0) {
    for (const r of rises) {
      const dx = r.end.x - r.start.x;
      const dy = r.end.y - r.start.y;
      const l2 = dx * dx + dy * dy;
      const t = l2 === 0 ? 1 : ((p.x - r.start.x) * dx + (p.y - r.start.y) * dy) / l2;
      if (t <= 0) return r.startElevation;
      if (t < 1) return r.startElevation + (r.endElevation - r.startElevation) * t;
    }
    return rises[rises.length - 1]!.endElevation;
  }
  const { roomById } = prepare(dungeon);
  const from = roomById.get(c.from);
  const to = roomById.get(c.to);
  const ef = roomElevation(from);
  const et = roomElevation(to);
  if (ef === et || !from || !to) return ef;
  const df = distToPolygon(p, from.shape);
  const dt = distToPolygon(p, to.shape);
  return df + dt === 0 ? ef : ef + ((et - ef) * df) / (df + dt);
}

/** Visual floor height (yards) at a ground-plane position. Pure and cheap (bbox-culled). */
export function groundHeightAt(dungeon: DungeonDef, x: number, y: number): Yards {
  const p = { x, y };
  const prep = prepare(dungeon);
  for (const r of prep.rooms) if (inArea(r, p)) return roomElevation(r.def);
  for (const c of prep.corridors) if (inArea(c, p)) return corridorHeightAt(dungeon, c.def, p);
  // Outside the walkable areas: take the nearest area's height.
  let best: { h: Yards; d: number } = { h: 0, d: Infinity };
  for (const r of prep.rooms) {
    const d = distToPolygon(p, r.poly);
    if (d < best.d) best = { h: roomElevation(r.def), d };
  }
  for (const c of prep.corridors) {
    const q = closestOnPolygon(p, c.poly);
    const d = Math.hypot(q.x - x, q.y - y);
    if (d < best.d) best = { h: corridorHeightAt(dungeon, c.def, q), d };
  }
  return best.h;
}

// ---- authoring helpers ---------------------------------------------------------

/** Distance along a→b (from a) at which the segment leaves the axis-aligned rect it starts in. */
function exitDistance(a: Vec2, b: Vec2, r: Rect): number {
  const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  const ux = (b.x - a.x) / len;
  const uy = (b.y - a.y) / len;
  let t = Infinity;
  if (ux > 1e-9) t = Math.min(t, (r.x + r.w - a.x) / ux);
  if (ux < -1e-9) t = Math.min(t, (r.x - a.x) / ux);
  if (uy > 1e-9) t = Math.min(t, (r.y + r.h - a.y) / uy);
  if (uy < -1e-9) t = Math.min(t, (r.y - a.y) / uy);
  return Math.max(0, Math.min(len, t));
}

const r2 = (v: number): number => Math.round(v * 100) / 100;
const along = (a: Vec2, b: Vec2, d: number): Vec2 => {
  const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  return { x: r2(a.x + ((b.x - a.x) / len) * d), y: r2(a.y + ((b.y - a.y) / len) * d) };
};

/**
 * The rise for a corridor whose centreline a→b runs from `fromRect` to `toRect` (both
 * axis-aligned room rects). The slope spans the part of the strip that touches neither room:
 * it starts where the whole cross-section has left `from` and ends where the strip first
 * touches `to`, so the floor is continuous everywhere (rooms win where they overlap the strip,
 * and every strip point outside the span is already at that room's level). For axis-aligned
 * corridors this is exactly the gap between the room edges. `lastYards` puts the slope in the
 * final N yards before the `to` room instead; `landing` leaves N flat yards at the top first.
 */
export function riseBetween(
  a: Vec2,
  b: Vec2,
  fromRect: Rect,
  toRect: Rect,
  fromElevation: Yards,
  toElevation: Yards,
  opts: { kind?: 'ramp' | 'stairs'; lastYards?: number; riser?: Yards; stripWidth?: Yards; landing?: Yards } = {},
): CorridorRise | null {
  if (fromElevation === toElevation) return null;
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  const nx = -(b.y - a.y) / (len || 1);
  const ny = (b.x - a.x) / (len || 1);
  const hw = (opts.stripWidth ?? 0) / 2;
  let s0 = 0;
  let s1 = len;
  for (const l of [-hw, 0, hw]) {
    const la = { x: a.x + nx * l, y: a.y + ny * l };
    const lb = { x: b.x + nx * l, y: b.y + ny * l };
    if (rectHasPoint(fromRect, la, 0)) s0 = Math.max(s0, exitDistance(la, lb, fromRect));
    if (rectHasPoint(toRect, lb, 0)) s1 = Math.min(s1, len - exitDistance(lb, la, toRect));
  }
  // Room corners poking into the strip touch it before any sampled line does.
  const ux = ny;
  const uy = -nx;
  const corners = (r: Rect): Vec2[] => [
    { x: r.x, y: r.y },
    { x: r.x + r.w, y: r.y },
    { x: r.x, y: r.y + r.h },
    { x: r.x + r.w, y: r.y + r.h },
  ];
  for (const c of corners(fromRect)) {
    const s = (c.x - a.x) * ux + (c.y - a.y) * uy;
    if (Math.abs((c.x - a.x) * nx + (c.y - a.y) * ny) <= hw && s > 0 && s < len) s0 = Math.max(s0, s);
  }
  for (const c of corners(toRect)) {
    const s = (c.x - a.x) * ux + (c.y - a.y) * uy;
    if (Math.abs((c.x - a.x) * nx + (c.y - a.y) * ny) <= hw && s > 0 && s < len) s1 = Math.min(s1, s);
  }
  if (s1 <= s0) s1 = s0 + 0.5;
  // A flat landing at the top (the `to` level) before the room: room for a door off the flight.
  if (opts.landing !== undefined) s1 = Math.max(s0 + 0.5, s1 - opts.landing);
  const start = opts.lastYards !== undefined ? Math.max(s0, s1 - opts.lastYards) : s0;
  const kind = opts.kind ?? 'stairs';
  const rise: CorridorRise = { kind, start: along(a, b, start), end: along(a, b, s1), startElevation: fromElevation, endElevation: toElevation };
  if (kind === 'stairs') rise.steps = Math.max(1, Math.round(Math.abs(toElevation - fromElevation) / (opts.riser ?? 0.25)));
  return rise;
}

const rectHasPoint = (r: Rect, p: Vec2, margin: number): boolean => p.x > r.x - margin && p.x < r.x + r.w + margin && p.y > r.y - margin && p.y < r.y + r.h + margin;

/**
 * A door across corridor a→b near one end (`at`), placed at the first spot ≥ `minGap` yd past
 * that room's edge where it cuts the strip cleanly: the strip on the `from` side of the door
 * never reaches the `to` room and vice versa, so a closed door always separates the rooms.
 * The door spans the strip plus 0.5 yd each side; `depth` is its blocking thickness.
 */
export function doorAcross(spec: {
  id: string;
  corridorId: string;
  a: Vec2;
  b: Vec2;
  fromRoom: RoomId;
  toRoom: RoomId;
  fromRect: Rect;
  toRect: Rect;
  stripWidth: Yards;
  at: 'from' | 'to';
  style: DoorStyle;
  initially?: 'open' | 'closed';
  sealsEncounter?: EncounterId;
  minGap?: Yards;
  depth?: Yards;
}): DoorDef {
  const { a, b } = spec;
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  const facing = Math.round(Math.atan2(b.y - a.y, b.x - a.x) * 1e4) / 1e4;
  const depth = spec.depth ?? DOOR_BLOCK_DEPTH;
  const half = depth / 2;
  const edgeFrom = exitDistance(a, b, spec.fromRect);
  const edgeTo = len - exitDistance(b, a, spec.toRect);
  const fx = Math.cos(facing);
  const fy = Math.sin(facing);
  const hw = spec.stripWidth / 2;
  const faceEnds = (c: Vec2, sd: number): Vec2[] => [-hw, 0, hw].map((l) => ({ x: c.x + fx * sd - fy * l, y: c.y + fy * sd + fx * l }));
  const cuts = (c: Vec2): boolean =>
    faceEnds(c, -half).every((q) => !rectHasPoint(spec.toRect, q, 0.3)) && faceEnds(c, half).every((q) => !rectHasPoint(spec.fromRect, q, 0.3));
  const gap0 = spec.minGap ?? 1;
  let pos: Vec2 | null = null;
  for (let g = gap0; g < len; g += 0.25) {
    const s = spec.at === 'from' ? edgeFrom + g : edgeTo - g;
    if (s - half <= 0 || s + half >= len) break;
    const c = along(a, b, s);
    if (cuts(c)) {
      pos = c;
      break;
    }
  }
  if (!pos) throw new Error(`doorAcross: no spot cuts ${spec.corridorId} cleanly for ${spec.id}`);
  const door: DoorDef = {
    id: spec.id,
    between: [spec.fromRoom, spec.toRoom],
    corridorId: spec.corridorId,
    position: pos,
    width: spec.stripWidth + 1,
    facing,
    style: spec.style,
    initially: spec.initially ?? 'open',
  };
  if (spec.depth !== undefined) door.depth = spec.depth;
  if (spec.sealsEncounter) door.sealsEncounter = spec.sealsEncounter;
  return door;
}
